import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { filesApi } from '@/lib/api';
import { useDevice } from '@/store/device';
import { FilesTable } from '@/components/FilesTable';
import { FilePreviewModal } from '@/components/FilePreviewModal';
import { TabStrip } from '@/components/logcat/TabStrip';
import { FilterBar, type SavedFilter } from '@/components/logcat/FilterBar';
import { LogList, type LogListHandle, type TokenField } from '@/components/logcat/LogList';
import { TokenMenu } from '@/components/logcat/TokenMenu';
import { SearchBar } from '@/components/logcat/SearchBar';
import { EventDrawer } from '@/components/logcat/EventDrawer';
import { useLogcatChunks, useLogcatStream } from '@/lib/logcat/useLogcatStream';
import {
  lineMatchesLevels,
  matchLine,
  parseFilter,
  toggleTagInExpr,
} from '@/lib/logcat/filter';
import { detectEvents, type LogEvent } from '@/lib/logcat/eventDetector';
import { EventStore } from '@/lib/logcat/eventStore';
import {
  aggregateConsecutive,
  mapRawIdxToDisplayIdx,
  type DisplayRow,
} from '@/lib/logcat/aggregate';
import type { LogLine } from '@/lib/logcat/parser';
import {
  ALL_LEVELS,
  DEFAULT_TAB,
  coerceSavedFilters,
  coerceTabs,
  newTabId,
  type LevelToggle,
  type TabConfig,
} from '@/lib/logcat/persistence';
import {
  AlertOctagon,
  ArrowDownToLine,
  ChevronsDown,
  ChevronsLeft,
  ChevronsUp,
  Layers,
  Pause,
  Pin,
  Play,
  RotateCcw,
  Save,
  Search,
  Trash2,
  WrapText,
  X,
} from 'lucide-react';
import type { FileMeta } from '@/lib/types';
import clsx from 'clsx';
import { CheckpointPanel } from '@/components/checkpoint/CheckpointPanel';
import { LineToRuleModal, type LineToRulePayload } from '@/components/checkpoint/LineToRuleModal';
import { RuleEntryBadge } from '@/components/checkpoint/RuleEntryBadge';
import { LogSelectionPopover, type LogSelectionPayload } from '@/components/checkpoint/LogSelectionPopover';
import { MultiSelectActionBar } from '@/components/checkpoint/MultiSelectActionBar';
import { useTabRunnerSet, type MountedSuite } from '@/lib/checkpoint/useTabRunnerSet';
import { checkpointApi } from '@/lib/checkpoint/api';
import type { Suite } from '@/lib/checkpoint/types';
import type { MountedRecord } from '@/components/checkpoint/CheckpointPanel';
import { WatchPanel } from '@/components/log-watch/WatchPanel';
import { useDeviceKeyWatchers } from '@/lib/log-watch/useDeviceKeyWatchers';
import { useDevicePrefs, useDevicePrefsUpdate } from '@/lib/myPrefs';

type Mode = 'live' | 'files';

const MATCH_RING = 100_000;
const RING_SIZE_DISPLAY = 5_000;
const CONTEXT_PRE = 200;
const CONTEXT_POST = 50;

export default function Logs() {
  const { serial, isOnline } = useDevice();
  const [mode, setMode] = useState<Mode>('live');
  const [openFile, setOpenFile] = useState<FileMeta | undefined>();

  const handleSaved = (meta: FileMeta) => {
    setOpenFile(meta);
    setMode('files');
  };

  return (
    // h-full + flex column so the live tab can fill the available height
    // entirely and we never produce a second outer scrollbar. The detail-
    // page wrapper already gives us a `flex-1 overflow-auto` slot; we
    // ask it to behave more like a viewport (no auto on us, only the
    // LogList scrolls inside). p-4 here used to live in `<Padded>` at
    // the route level, but Padded broke the h-full chain.
    <div className="flex h-full flex-col gap-2 overflow-hidden p-4">
      <header className="flex items-center justify-between gap-3">
        <h1 className="text-base font-semibold tracking-tight">日志 · Logcat</h1>
        <div className="inline-flex rounded-md border border-line bg-bg-card p-0.5">
          {(['live', 'files'] as Mode[]).map((m) => (
            <button
              key={m}
              className={clsx(
                'px-2.5 py-1 text-xs',
                mode === m ? 'rounded bg-accent text-white' : 'text-ink-muted'
              )}
              onClick={() => setMode(m)}
            >
              {m === 'live' ? '实时日志' : '已保存文件'}
            </button>
          ))}
        </div>
      </header>

      {mode === 'live' && (
        <LiveLogcat serial={serial} isOnline={isOnline} onSaved={handleSaved} />
      )}
      {mode === 'files' && (
        <div className="flex-1 overflow-auto">
          <SavedLogs serial={serial} openFile={openFile} setOpenFile={setOpenFile} />
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Live logcat: multi-tab, AS-style                                     */
/* -------------------------------------------------------------------- */

interface LiveProps {
  serial: string | undefined;
  isOnline: boolean;
  onSaved: (meta: FileMeta) => void;
}

function LiveLogcat({ serial, isOnline, onSaved }: LiveProps) {
  // B2 prefs：tabs / savedFilters per-device。
  // serial 缺省时给一个空字符串挂在 hooks（值不会被读，store 内部按未存在处理）。
  const prefSerial = serial ?? '';
  const logPrefs = useDevicePrefs(prefSerial, (d) => d.log as {
    tabs?: unknown;
    filters?: unknown;
    activeTabId?: string;
  });
  const updateDevice = useDevicePrefsUpdate(prefSerial);

  const tabs: TabConfig[] = useMemo(() => {
    if (!serial) return [];
    const parsed = coerceTabs(logPrefs.tabs);
    if (parsed.length > 0) return parsed;
    return [{ id: newTabId(), ...DEFAULT_TAB }];
  }, [serial, logPrefs.tabs]);

  const savedFilters: SavedFilter[] = useMemo(
    () => coerceSavedFilters(logPrefs.filters),
    [logPrefs.filters],
  );

  const [activeIdState, setActiveIdState] = useState<string>(() => logPrefs.activeTabId ?? '');
  // Reconcile activeId whenever the tab list shifts (serial change / rename).
  const activeId = tabs.some((t) => t.id === activeIdState) ? activeIdState : tabs[0]?.id ?? '';

  // Bootstrap: if user has no persisted tabs yet, push the synthesised
  // default once so subsequent renames/edits target a stable id rather
  // than spawning fresh tab-${ts} ids on every render.
  useEffect(() => {
    if (!serial) return;
    if (!Array.isArray(logPrefs.tabs) || logPrefs.tabs.length === 0) {
      updateDevice({ log: { tabs } });
    }
  }, [serial, logPrefs.tabs, tabs, updateDevice]);

  const setTabs = useCallback(
    (next: TabConfig[] | ((prev: TabConfig[]) => TabConfig[])) => {
      const resolved = typeof next === 'function' ? next(tabs) : next;
      updateDevice({ log: { tabs: resolved } });
    },
    [tabs, updateDevice],
  );

  const setActiveId = useCallback(
    (id: string) => {
      setActiveIdState(id);
      updateDevice({ log: { activeTabId: id } });
    },
    [updateDevice],
  );

  const updateTab = (id: string, patch: Partial<TabConfig>): void => {
    setTabs((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  };

  const addTab = (): void => {
    const t: TabConfig = { id: newTabId(), ...DEFAULT_TAB, name: `Logcat (${tabs.length + 1})` };
    setTabs([...tabs, t]);
    setActiveId(t.id);
  };

  const closeTab = (id: string): void => {
    const next = tabs.filter((t) => t.id !== id);
    setTabs(next);
    if (activeId === id && next.length > 0) setActiveId(next[next.length - 1].id);
  };

  const renameTab = (id: string, name: string): void => updateTab(id, { name });

  const persistFilters = (next: SavedFilter[]): void => {
    updateDevice({ log: { filters: next } });
  };

  if (!serial) {
    return <div className="card-pad text-sm text-ink-muted">请在右上角选择一台设备开始。</div>;
  }
  if (tabs.length === 0) {
    return null;
  }

  const active = tabs.find((t) => t.id === activeId) ?? tabs[0];

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-2">
      {!isOnline && (
        <div className="card-pad border-amber-500 text-xs text-amber-600">
          手机已离线，logcat 无法采集；可切换到「已保存文件」回看历史日志。
        </div>
      )}
      <TabStrip
        tabs={tabs.map((t) => ({ id: t.id, name: t.name, active: t.expr.trim().length > 0 }))}
        activeId={active.id}
        onSelect={setActiveId}
        onClose={closeTab}
        onAdd={addTab}
        onRename={renameTab}
      />
      <LogcatTabBody
        key={active.id}
        serial={serial}
        isOnline={isOnline}
        tab={active}
        onChange={(patch) => updateTab(active.id, patch)}
        savedFilters={savedFilters}
        onSaveFilter={() => {
          const expr = active.expr.trim();
          if (!expr) return;
          const name = prompt('为该过滤表达式起个名字：', `${active.name} 过滤`);
          if (!name) return;
          const sf: SavedFilter = { id: newTabId(), name, expr };
          persistFilters([...savedFilters, sf]);
        }}
        onApplySaved={(sf) => updateTab(active.id, { expr: sf.expr })}
        onDeleteSaved={(id) => persistFilters(savedFilters.filter((s) => s.id !== id))}
        onSaved={onSaved}
      />
    </div>
  );
}

interface BodyProps {
  serial: string;
  isOnline: boolean;
  tab: TabConfig;
  onChange: (patch: Partial<TabConfig>) => void;
  savedFilters: SavedFilter[];
  onSaveFilter: () => void;
  onApplySaved: (sf: SavedFilter) => void;
  onDeleteSaved: (id: string) => void;
  onSaved: (meta: FileMeta) => void;
}

type RightPanelTab = 'checkpoint' | 'watch';

interface CheckpointDevicePrefs {
  panelVisible?: boolean;
  rightPanelTab?: RightPanelTab;
  /** Mounted suites keyed by logcat tab id. */
  mountedByTab?: Record<string, MountedRecord[]>;
}

function LogcatTabBody({
  serial,
  isOnline,
  tab,
  onChange,
  savedFilters,
  onSaveFilter,
  onApplySaved,
  onDeleteSaved,
  onSaved,
}: BodyProps) {
  const stream = useLogcatStream(isOnline ? serial : undefined);
  const [busy, setBusy] = useState(false);

  /* ---------- Phase 14：日志检查点引擎（B2 prefs.devices[serial].checkpoint） ---------- */
  const checkpointPrefs = useDevicePrefs(
    serial,
    (d) => d.checkpoint as CheckpointDevicePrefs,
  );
  const updateDevice = useDevicePrefsUpdate(serial);

  const panelVisible = !!checkpointPrefs.panelVisible;
  const setPanelVisible = useCallback(
    (v: boolean | ((prev: boolean) => boolean)) => {
      const next = typeof v === 'function' ? v(panelVisible) : v;
      updateDevice({ checkpoint: { panelVisible: next } });
    },
    [panelVisible, updateDevice],
  );

  const mountedSuites: MountedRecord[] = useMemo(() => {
    const raw = checkpointPrefs.mountedByTab?.[tab.id];
    if (!Array.isArray(raw)) return [];
    // 同一份 suite 只显一张卡：rehydrate 时保留每个 suiteId 最新的一条。
    const dedup = new Map<string, MountedRecord>();
    for (const m of raw) {
      if (m && typeof m.suiteId === 'string') dedup.set(m.suiteId, m);
    }
    return Array.from(dedup.values());
  }, [checkpointPrefs.mountedByTab, tab.id]);
  const setMountedSuites = useCallback(
    (next: MountedRecord[] | ((prev: MountedRecord[]) => MountedRecord[])) => {
      const resolved = typeof next === 'function' ? next(mountedSuites) : next;
      updateDevice({ checkpoint: { mountedByTab: { [tab.id]: resolved } } });
    },
    [mountedSuites, tab.id, updateDevice],
  );

  const [suiteCache, setSuiteCache] = useState<Record<string, Suite>>({});

  // 拉所有 mounted 的 suite 详情
  const [suiteFetchBusy, setSuiteFetchBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const missing = mountedSuites.filter((m) => !suiteCache[m.suiteId]);
    if (missing.length === 0) return;
    setSuiteFetchBusy(true);
    Promise.all(
      missing.map(async (m) => {
        try {
          const detail = await checkpointApi.getSuite(m.suiteId);
          return [m.suiteId, detail.suite] as const;
        } catch {
          return null;
        }
      }),
    )
      .then((entries) => {
        if (cancelled) return;
        setSuiteCache((prev) => {
          const next = { ...prev };
          for (const e of entries) if (e) next[e[0]] = e[1];
          return next;
        });
      })
      .finally(() => {
        if (!cancelled) setSuiteFetchBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mountedSuites, suiteCache]);

  const mountedForRunner: MountedSuite[] = useMemo(
    () =>
      mountedSuites
        .filter((m) => suiteCache[m.suiteId])
        .map((m) => ({
          runId: m.runId,
          suite: suiteCache[m.suiteId]!,
          params: m.params,
          active: m.active,
        })),
    [mountedSuites, suiteCache],
  );
  const checkpointController = useTabRunnerSet({
    serial: isOnline ? serial : undefined,
    mounted: mountedForRunner,
    historyLines: stream.lines,
  });

  /* ---------- Phase 14.1：日志摘要面板（Log Watch，M2 简化版） ---------- */
  const panelTab: RightPanelTab =
    checkpointPrefs.rightPanelTab === 'watch' ? 'watch' : 'checkpoint';
  const setPanelTab = useCallback(
    (next: RightPanelTab) => {
      updateDevice({ checkpoint: { rightPanelTab: next } });
    },
    [updateDevice],
  );

  // M2 第二轮：keys 列表 per 设备共享（同 serial 多 logcat tab + 同浏览器
  // 多浏览器 tab 实时同步，基于 storage 事件）。watch hook 内部跑一个固定
  // view 的 RunnerSet。
  const watchController = useDeviceKeyWatchers({
    serial: isOnline ? serial : undefined,
    historyLines: stream.lines,
  });
  const watchTotalHits = watchController.totalHits;

  // 当前 tab 包名提示（从 expr 中粗略抽 `package:com.foo.bar`）
  const currentPackage = useMemo(() => {
    const m = /package:([A-Za-z0-9_.\-]+)/.exec(tab.expr);
    return m?.[1];
  }, [tab.expr]);

  /**
   * 行 marker 缓存 + 稳定函数引用。
   *
   * 没缓存前两个反复触发整窗重渲染的源头：
   *   1) `checkpointController` 每个 chunk 都新引用（useTabRunnerSet 内
   *      `setNextLineId` 每 chunk 自增），导致 `markerFor` 用 useCallback
   *      也每帧都是新引用 → LogList 的 `itemData` 永远是新对象 → 整窗 child
   *      虽走 memo 但 reconciler 仍要 walk；
   *   2) markerFor 自己计算并不便宜（每行一次 `hitsAtFn` + 严重度 reduce
   *      + 标题字符串拼接）；wrap 模式下 visible window ~30–80 行每次重渲
   *      染都跑一遍。
   *
   * 解法：用 `WeakMap<LogLine, MarkerInfo | null>` 按 line 引用缓存——
   * `aggregateConsecutive` 已经保证大量 line 引用稳定，命中后直接返回。
   * 只在 `checkpointController.snapshots` 真的变（规则状态翻转、超时等
   * 真正改变 marker 颜色的场景）时才整体清缓存。`nextLineId` 自增不清。
   *
   * 同时把 markerFor 包成 ref + 稳定 wrapper：暴露给 LogList 的引用永远
   * 不变，`itemData` 不会因 markerFor churn。
   */
  type MarkerInfo = { color: string; title: string } | null;
  const markerCacheRef = useRef<WeakMap<LogLine, MarkerInfo>>(new WeakMap());
  useEffect(() => {
    markerCacheRef.current = new WeakMap();
  }, [checkpointController.snapshots]);

  const computeMarkerRef = useRef<(line: LogLine) => MarkerInfo>(() => null);
  computeMarkerRef.current = (line: LogLine): MarkerInfo => {
    if (typeof line.id !== 'number') return null;
    const hits = checkpointController.hitsAtFn(line.id);
    if (hits.length === 0) return null;
    // 多命中：按最严重等级取色
    const severity = hits.reduce(
      (acc, h) => {
        if (h.state === 'fail' || h.state === 'timeout') return 'fail';
        if (h.state === 'mid' && acc !== 'fail') return 'mid';
        return acc;
      },
      'pass' as 'pass' | 'mid' | 'fail',
    );
    const color = severity === 'fail' ? '#f43f5e' : severity === 'mid' ? '#f59e0b' : '#10b981';
    const titleLines = hits.map((h) => `· ${h.ruleId} ${h.state.toUpperCase()} ${h.summary}`);
    return { color, title: `命中 ${hits.length} 条规则\n${titleLines.join('\n')}` };
  };

  const markerFor = useCallback((line: LogLine): MarkerInfo => {
    const cache = markerCacheRef.current;
    const cached = cache.get(line);
    if (cached !== undefined) return cached;
    const fresh = computeMarkerRef.current(line);
    cache.set(line, fresh);
    return fresh;
  }, []);

  // 行右键菜单 → 打开 LineToRuleModal
  const [modalPayload, setModalPayload] = useState<LineToRulePayload | null>(null);
  const handleRowContextMenu = useCallback((line: LogLine) => {
    setModalPayload({ mode: 'single-line', line });
    return true;
  }, []);

  // 检查点徽章聚合
  const checkpointSummary = useMemo(() => {
    let pass = 0;
    let mid = 0;
    let fail = 0;
    for (const s of checkpointController.snapshots) {
      switch (s.overallState) {
        case 'pass':
          pass += 1;
          break;
        case 'mid':
          mid += 1;
          break;
        case 'fail':
          fail += 1;
          break;
        default:
          break;
      }
    }
    return { pass, mid, fail };
  }, [checkpointController.snapshots]);

  // 多选状态：Shift / Cmd+Click 收集行 index
  const [multiSelected, setMultiSelected] = useState<Set<number>>(() => new Set());
  useEffect(() => {
    // 切 tab 时清空多选，避免索引串台
    setMultiSelected(new Set());
  }, [tab.id]);

  const handleMultiSelectChange = useCallback((next: Set<number>) => {
    setMultiSelected(next);
  }, []);

  // 选区浮动工具条
  const [selectionPayload, setSelectionPayload] = useState<LogSelectionPayload | null>(null);
  const dismissSelection = useCallback((): void => {
    setSelectionPayload(null);
    if (typeof window !== 'undefined') {
      window.getSelection()?.removeAllRanges();
    }
  }, []);

  const compiled = useMemo(() => parseFilter(tab.expr), [tab.expr]);
  const hasFilter = compiled.terms.length > 0;

  // tabLevels 缓存：避免走 fallback 时每次 render 都新建 ALL_LEVELS 引用。
  // 直接复用常量数组 — 下游只读消费，不需要拷贝防御。
  const tabLevels = useMemo<LevelToggle[]>(
    () => (tab.levels && tab.levels.length > 0 ? tab.levels : ALL_LEVELS),
    [tab.levels],
  );
  const levelsKey = tabLevels.join('');

  const [matched, setMatched] = useState<LogLine[]>([]);
  const [matchedTotal, setMatchedTotal] = useState(0);

  // 把 tab.combine 抽出来作为 reseed / chunk filter 的依赖，缺省 'and' 与历
  // 史一致。useEffect 用它做 deps 才能在 AND/OR 切换时立刻 re-seed。
  const tabCombine = tab.combine ?? 'and';

  useEffect(() => {
    if (!hasFilter) {
      setMatched([]);
      setMatchedTotal(0);
      return;
    }
    const seed: LogLine[] = [];
    for (const l of stream.lines) {
      if (matchLine(l, compiled.terms, {}, tabCombine)) seed.push(l);
    }
    const trimmed = seed.length > MATCH_RING ? seed.slice(-MATCH_RING) : seed;
    setMatched(trimmed);
    setMatchedTotal(seed.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compiled.terms, hasFilter, tabCombine]);

  useLogcatChunks(isOnline && hasFilter ? serial : undefined, (chunk, cleared) => {
    if (cleared) {
      setMatched([]);
      setMatchedTotal(0);
      return;
    }
    if (chunk.length === 0) return;
    const adds: LogLine[] = [];
    for (const l of chunk) {
      if (matchLine(l, compiled.terms, {}, tabCombine)) adds.push(l);
    }
    if (adds.length === 0) return;
    setMatchedTotal((n) => n + adds.length);
    setMatched((prev) => {
      const next =
        prev.length + adds.length > MATCH_RING
          ? prev.slice(prev.length + adds.length - MATCH_RING)
          : prev.slice();
      next.push(...adds);
      return next;
    });
  });

  /**
   * 一步到位的过滤：expr term 过滤 + level toggle 合并到同一个 useMemo。
   * src 取 `matched`（有 expr）或 `stream.lines`（无 expr）；level 全开则
   * 直接返 src 引用稳定。这样只有 stream.lines 引用真正变（clear）或
   * matched 数组变 / levels 切换时才重建 filtered。
   */
  const filtered = useMemo(() => {
    const src = hasFilter ? matched : stream.lines;
    if (tabLevels.length === ALL_LEVELS.length) return src;
    return src.filter((l) => lineMatchesLevels(l, tabLevels));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFilter, matched, stream.lines, levelsKey]);

  /* ---------- 冻结视图：拆分 manualPaused / pinPaused ----------------
   * 历史包袱：原先一个 `tab.paused` 字段同时表示
   *   (a) 用户主动按工具栏「冻结」按钮 — 想持久化（关 tab 重开仍冻结）；
   *   (b) 点击行临时锁定 — 不应当持久化（关 tab 重开应回 live）。
   * 当时用 module ref `autoPausedByPinRef` 区分两者，但 hot-reload /
   * 重 mount 时 ref 丢，重开 tab 时变成"冻结但没锁定行 + 没法解锁"。
   *
   * 现在拆开：
   *   - manualPaused = tab.paused → 只承载 (a)，仍持久化到 prefs；
   *   - pinPaused = local useState → 只承载 (b)，不持久化；
   *   - pausedForView = manualPaused || pinPaused → 视图层只看派生值。
   * 解锁 pin 时直接 setPinPaused(false)，不再需要 module ref 协调。
   */
  const manualPaused = tab.paused;
  const [pinPaused, setPinPaused] = useState(false);
  const pausedForView = manualPaused || pinPaused;

  /* ---------- pause-while-frozen backlog counter ---------- */
  const [pauseBacklog, setPauseBacklog] = useState(0);
  const wasPausedRef = useRef(pausedForView);
  useEffect(() => {
    if (!pausedForView && wasPausedRef.current) {
      setPauseBacklog(0);
    }
    wasPausedRef.current = pausedForView;
  }, [pausedForView]);
  useLogcatChunks(isOnline && pausedForView ? serial : undefined, (chunk, cleared) => {
    if (cleared) {
      setPauseBacklog(0);
      return;
    }
    if (chunk.length === 0) return;
    setPauseBacklog((n) => n + chunk.length);
  });

  /* ---------- event detection ---------- */
  const eventStoreRef = useRef<EventStore>();
  if (!eventStoreRef.current) eventStoreRef.current = new EventStore();
  const eventStore = eventStoreRef.current;
  const [eventDrawerOpen, setEventDrawerOpen] = useState(false);
  const [eventBadge, setEventBadge] = useState(0);
  useEffect(() => {
    return eventStore.subscribe(() => {
      setEventBadge(eventStore.snapshot().unread);
    });
  }, [eventStore]);
  const slidingTailRef = useRef<LogLine[]>([]);
  const detectedHighWaterRef = useRef<number>(0);
  // Seed once from current shared buffer (covers tab init / device switch).
  useEffect(() => {
    const seed = stream.lines.slice(-300);
    slidingTailRef.current = seed.slice();
    detectedHighWaterRef.current = seed.length;
    const ev = detectEvents({ baseIdx: 0, lines: seed });
    if (ev.length > 0) eventStore.push(ev);
    return () => eventStore.clearAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serial]);
  useLogcatChunks(isOnline ? serial : undefined, (chunk, cleared) => {
    if (cleared) {
      slidingTailRef.current = [];
      detectedHighWaterRef.current = 0;
      eventStore.clearAll();
      return;
    }
    if (chunk.length === 0) return;
    const tail = slidingTailRef.current;
    const merged = tail.concat(chunk);
    const baseIdx = detectedHighWaterRef.current - tail.length;
    const ev = detectEvents({
      baseIdx,
      lines: merged,
      minStartIdx: detectedHighWaterRef.current,
    });
    if (ev.length > 0) eventStore.push(ev);
    detectedHighWaterRef.current += chunk.length;
    // Keep ~250 lines of context for the next detection round.
    const next = merged.slice(-250);
    slidingTailRef.current = next;
  });

  /* ---------- tag 智能提示池：所有出现过的 tag ----------
   * 给 FilterBar 喂智能提示用。增量收集（每 chunk 只看新行的 tag，不
   * 扫整 buffer），rAF 节流 setState 避免 setMatched 同一帧 flush 时
   * 多次重渲染。stream cleared 时同步清空。
   *
   * 收集口径：所有历史上看到过的 tag（即使 ring evict 把那些行刷掉
   * 了），用户在过滤建议里能找到自己曾经见过的 tag，比"只看当前 ring
   * 窗口里的 tag"更实用。重新 attach（切设备/serial）时整池清空。 */
  const tagSetRef = useRef<Set<string>>(new Set());
  const tagFlushRafRef = useRef<number | null>(null);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  useEffect(() => {
    // 切 serial 时重置（hook 的 useEffect cleanup 不够，因为 setRef
    // 是模块级状态；这里手动清）
    tagSetRef.current = new Set();
    setTagSuggestions([]);
    return () => {
      if (tagFlushRafRef.current !== null) {
        cancelAnimationFrame(tagFlushRafRef.current);
        tagFlushRafRef.current = null;
      }
    };
  }, [serial]);
  useLogcatChunks(isOnline ? serial : undefined, (chunk, cleared) => {
    if (cleared) {
      tagSetRef.current = new Set();
      if (tagFlushRafRef.current !== null) {
        cancelAnimationFrame(tagFlushRafRef.current);
        tagFlushRafRef.current = null;
      }
      setTagSuggestions([]);
      return;
    }
    let added = false;
    for (const ln of chunk) {
      const t = ln.tag;
      if (t && !tagSetRef.current.has(t)) {
        tagSetRef.current.add(t);
        added = true;
      }
    }
    if (added && tagFlushRafRef.current === null) {
      tagFlushRafRef.current = requestAnimationFrame(() => {
        tagFlushRafRef.current = null;
        // 按字母排序快照；列表大小有限（典型 < 500 个），代价小。
        const arr = Array.from(tagSetRef.current).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' }),
        );
        setTagSuggestions(arr);
      });
    }
  });

  /* ---------- list ref + sticky / pendingNew ---------- */
  const listRef = useRef<LogListHandle>(null);
  const [sticky, setSticky] = useState(true);
  const [pendingNew, setPendingNew] = useState(0);
  /**
   * Pinned row anchor. 用 LogLine.id（全局单调递增的行号）作为锚点，而不是
   * displayRows 下标，因为 ring evict / aggregate 重构都会让下标整体左移而
   * id 不变；这样用户点过的那一行就算前面被丢了一些行视觉位置依然稳定。
   *
   * `selectedIndex` 由当前 displayRows 反查得来：lineId 还在视图里 → 找到
   * 对应下标；否则 null（自动解除锁定状态）。LogList / 横幅都吃这个推导值。
   */
  const [selectedLineId, setSelectedLineId] = useState<number | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedLineId !== null) {
        setSelectedLineId(null);
        setPinPaused(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedLineId]);

  /* ---------- viewport size tracking ----------------------------------
   * react-window's FixedSizeList needs an explicit pixel height. Before
   * we used `window.innerHeight - 280` which was wrong on small screens
   * and bad on resize. Now we measure the actual wrapper with ResizeObserver
   * and subtract the inline footer (height ~28px including padding). */
  const logViewportRef = useRef<HTMLDivElement | null>(null);
  const [viewportH, setViewportH] = useState<number>(0);
  useEffect(() => {
    const el = logViewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      setViewportH(r.height);
    });
    ro.observe(el);
    const r = el.getBoundingClientRect();
    setViewportH(r.height);
    return () => ro.disconnect();
  }, []);
  // Inline footer takes ~28px (px-3 py-1 + 1px top border + line-height).
  // Clamp to a sane minimum so the list still draws something on tiny
  // windows.
  const logListHeight = Math.max(viewportH - 28, 160);

  /* ---------- instant-snap on user-initiated layout flips ---------- */
  // Toggling wrap/aggregate changes itemCount/row layout dramatically, so
  // we snap to the tail to keep the user oriented. Freezing must NOT
  // snap: the whole point of freeze is to hold the current vantage. Only
  // UNFREEZE (paused: true → false) snaps the user forward to catch up.
  const firstSnapRenderRef = useRef(true);
  // (a) aggregate / wrap toggle -> snap to bottom (no animation)
  useEffect(() => {
    if (firstSnapRenderRef.current) return; // initial mount handled by (b)
    const id = requestAnimationFrame(() => {
      listRef.current?.scrollToBottom(false);
    });
    return () => cancelAnimationFrame(id);
  }, [tab.aggregate, tab.wrap]);
  // (b) paused — only snap on UNFREEZE direction. Going INTO freeze keeps
  // the viewport exactly where it is, which is what the user just told us
  // they want by clicking 冻结. 跟 pausedForView (manual + pin 合并值) 走，
  // 这样 pin 解锁回到 live 也能正确 snap。
  const prevPausedRef = useRef(pausedForView);
  useEffect(() => {
    const wasPaused = prevPausedRef.current;
    prevPausedRef.current = pausedForView;
    if (firstSnapRenderRef.current) {
      firstSnapRenderRef.current = false;
      return;
    }
    if (wasPaused && !pausedForView) {
      const id = requestAnimationFrame(() => {
        listRef.current?.scrollToBottom(false);
      });
      return () => cancelAnimationFrame(id);
    }
  }, [pausedForView]);

  const buildPrefix = (): string => {
    const parts = ['live'];
    if (tab.expr.trim()) {
      const cleaned = tab.expr
        .replace(/[^A-Za-z0-9._:~|=-]+/g, '_')
        .replace(/\.\.+/g, '_')
        .slice(0, 60);
      if (cleaned) parts.push(cleaned);
    }
    return parts.join('-');
  };

  const onSaveCurrent = async () => {
    if (filtered.length === 0) return;
    setBusy(true);
    try {
      const r = await filesApi.saveLog({
        serial,
        lines: filtered.map((l) => l.raw),
        prefix: buildPrefix(),
      });
      onSaved(r.meta);
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /* ---------- visible lines (with pause snapshot) ----------------------
   * 实例级冻结快照：pausedForView 翻 true 时 capture 一次 filtered.slice()，
   * 期间 filtered 引用不论怎么抖（prefs round-trip / chunk push / aggregate
   * 重算）都返回这份快照；翻 false 才释放。这避免了原模块级 frozenCache 多
   * 实例互抢、以及"半冻结"现象（filtered 引用一变就重 slice，视图悄悄被新
   * filtered 顶替的 race）。 */
  const frozenLinesRef = useRef<LogLine[] | null>(null);
  const visibleLines = useMemo<LogLine[]>(() => {
    if (!pausedForView) {
      frozenLinesRef.current = null;
      return filtered;
    }
    if (!frozenLinesRef.current) {
      frozenLinesRef.current = filtered.slice();
    }
    return frozenLinesRef.current;
  }, [pausedForView, filtered]);

  /* ---------- aggregation (fold consecutive duplicates) ---------- */
  const aggregateOn = tab.aggregate !== false;
  // Hold the previous aggregated result so the aggregator can reuse rows
  // by reference (React.memo bail-out for the rows the user is staring at,
  // selection preservation, smaller GC pressure).
  const prevDisplayRowsRef = useRef<DisplayRow[]>([]);
  // CRITICAL: `visibleLines` may be the SAME reference across renders
  // when the underlying stream pushes new items in place. Adding `length`
  // and `stream.version` to the deps array forces re-aggregation when
  // either grows even though the reference is stable.
  const displayRows: DisplayRow[] = useMemo(() => {
    let out: DisplayRow[];
    if (!aggregateOn) {
      // Cheap no-op wrapping into the DisplayRow shape so the list type stays
      // uniform. count=1 means "no badge"; srcStart/End point to self.
      out = new Array(visibleLines.length);
      for (let i = 0; i < visibleLines.length; i++) {
        const l = visibleLines[i];
        out[i] = { ...l, count: 1, firstTs: l.ts, lastTs: l.ts, srcStart: i, srcEnd: i };
      }
    } else {
      out = aggregateConsecutive(visibleLines, prevDisplayRowsRef.current);
    }
    prevDisplayRowsRef.current = out;
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleLines, visibleLines.length, stream.version, aggregateOn]);
  /**
   * `selectedLineId` → 当前 displayRows 中的下标。从尾向前找（用户最近选的
   * 行通常在尾部附近），并兼容 aggregate=on 时多条原始行折叠到一个
   * DisplayRow 的情况：DisplayRow.id 取自首行，但 srcStart/srcEnd 内任一
   * 原始行被点中都应当映射到这个 row。
   *
   * 由于 displayRows 不含 srcStart/srcEnd 之外的 id 索引，我们退化为：
   * 直接比对 row.id；用户体感上只锁首行，足够。lineId 已被 ring evict 时
   * 返回 null，UI 自动解锁。
   */
  const selectedIndex = useMemo<number | null>(() => {
    if (selectedLineId === null) return null;
    for (let i = displayRows.length - 1; i >= 0; i -= 1) {
      if (displayRows[i].id === selectedLineId) return i;
    }
    return null;
  }, [selectedLineId, displayRows]);
  /** Total folded count (sum of counts) — for the footer line counter. */
  const foldedTotal = useMemo(() => {
    if (!aggregateOn) return displayRows.length;
    let n = 0;
    for (const r of displayRows) n += r.count;
    return n;
  }, [displayRows, aggregateOn]);

  /* ---------- 选区监听 ---------- */
  /**
   * mouseup 时检查 logViewportRef 内是否有非空 selection；如有，反推
   * 起止行 index 与跨行序列，写入 selectionPayload。
   *
   * 跳过条件：
   *   - selection 任一端不在 logViewportRef 内 → 用户在面板里选了文字；
   *   - selection 完全在某个按钮内（例如 token menu trigger）→ 让原 click 走完。
   */
  useEffect(() => {
    const el = logViewportRef.current;
    if (!el) return;
    const onMouseUp = (e: MouseEvent): void => {
      // 给浏览器一点时间提交选区
      setTimeout(() => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
        const range = sel.getRangeAt(0);
        if (!el.contains(range.startContainer) || !el.contains(range.endContainer)) return;
        const text = sel.toString();
        if (!text.trim()) return;
        const startNode = range.startContainer instanceof HTMLElement
          ? range.startContainer
          : range.startContainer.parentElement;
        const endNode = range.endContainer instanceof HTMLElement
          ? range.endContainer
          : range.endContainer.parentElement;
        const startRow = startNode?.closest<HTMLElement>('[data-line-index]');
        const endRow = endNode?.closest<HTMLElement>('[data-line-index]');
        if (!startRow || !endRow) return;
        const startIdx = parseInt(startRow.getAttribute('data-line-index') || '-1', 10);
        const endIdxRaw = parseInt(endRow.getAttribute('data-line-index') || '-1', 10);
        if (Number.isNaN(startIdx) || startIdx < 0) return;
        const endIdx = Number.isNaN(endIdxRaw) ? startIdx : endIdxRaw;
        const lo = Math.min(startIdx, endIdx);
        const hi = Math.max(startIdx, endIdx);
        const startLine = displayRows[lo];
        const endLine = displayRows[hi];
        if (!startLine || !endLine) return;
        const spanned: LogLine[] = [];
        for (let i = lo; i <= hi && i < displayRows.length; i += 1) {
          spanned.push(displayRows[i]);
        }
        setSelectionPayload({
          startLineIdx: lo,
          endLineIdx: hi,
          startLine,
          endLine,
          text,
          anchor: { x: e.clientX, y: e.clientY },
          spannedLines: spanned,
        });
      }, 0);
    };
    el.addEventListener('mouseup', onMouseUp);
    return () => el.removeEventListener('mouseup', onMouseUp);
  }, [displayRows]);

  /* ---------- in-list search ---------- */
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchActive, setSearchActive] = useState(-1);
  const searchMatches = useMemo(() => {
    if (!searchOpen || searchQuery.length === 0) return [] as number[];
    const q = searchQuery.toLowerCase();
    const idx: number[] = [];
    for (let i = 0; i < displayRows.length; i++) {
      if (displayRows[i].raw.toLowerCase().includes(q)) idx.push(i);
    }
    return idx;
  }, [searchOpen, searchQuery, displayRows]);
  useEffect(() => {
    if (searchMatches.length === 0) {
      setSearchActive(-1);
    } else if (searchActive < 0 || searchActive >= searchMatches.length) {
      setSearchActive(searchMatches.length - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchMatches.length]);
  const stepSearch = useCallback(
    (delta: 1 | -1) => {
      if (searchMatches.length === 0) return;
      const next =
        (searchActive + delta + searchMatches.length) % searchMatches.length;
      setSearchActive(next);
      listRef.current?.scrollToIndex(searchMatches[next], 'center');
      listRef.current?.pulseIndex(searchMatches[next]);
    },
    [searchActive, searchMatches]
  );
  // Keyboard: Ctrl/Cmd+F to open, F3 / Cmd+G to step.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.ctrlKey || e.metaKey;
      if (meta && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        setSearchOpen(true);
      } else if (e.key === 'F3' || (meta && e.key.toLowerCase() === 'g')) {
        if (searchOpen && searchMatches.length > 0) {
          e.preventDefault();
          stepSearch(e.shiftKey ? -1 : 1);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [searchOpen, searchMatches.length, stepSearch]);

  /* ---------- token menu ---------- */
  const [tokenMenu, setTokenMenu] = useState<{
    field: TokenField;
    value: string;
    rect: { top: number; bottom: number; left: number; right: number };
  } | null>(null);

  const handleTokenClick = useCallback(
    (
      field: TokenField,
      value: string,
      rect: { top: number; bottom: number; left: number; right: number }
    ) => {
      setTokenMenu({ field, value, rect });
    },
    []
  );

  // useCallback so TokenMenu's `useEffect([onClose])` chain doesn't tear
  // down and re-add the `mousedown` window listener on every parent
  // render (heavy under streaming traffic). The deps are everything
  // this body actually reads — current tab settings + menu target.
  const onTokenPick = useCallback(
    (action: 'only' | 'exclude' | 'include' | 'copy') => {
      if (!tokenMenu) return;
      const { field, value } = tokenMenu;
      if (action === 'copy') {
        void navigator.clipboard.writeText(value).catch(() => {});
      } else if (field === 'level') {
        // level 仍走 only/exclude 两选一（'include' 不会从 TokenMenu 这里 emit）
        if (action === 'only') {
          onChange({ levels: [value as LevelToggle] });
        } else if (action === 'exclude') {
          const next = tabLevels.filter((l) => l !== (value as LevelToggle));
          onChange({ levels: next.length > 0 ? next : ALL_LEVELS.slice() });
        }
      } else if (field === 'tag') {
        // tag 字段：右键 popover 的 include / exclude 都走 toggle，与左键
        // 单击保持同一套语义（多值 `|` 拼、再点一次移除、空了整 token 删）。
        if (action === 'include') {
          onChange({ expr: toggleTagInExpr(tab.expr, value, 'include') });
        } else if (action === 'exclude') {
          onChange({ expr: toggleTagInExpr(tab.expr, value, 'exclude') });
        }
      } else {
        // pid：保留旧"追加单值 token"语义（pid 几乎都是唯一确定值，没有
        // 像 tag 那样需要批量 toggle 的诉求）
        const term =
          action === 'only' ? `pid:${quoteIfSpace(value)}` : `-pid:${quoteIfSpace(value)}`;
        const next = tab.expr.trim().length === 0 ? term : `${tab.expr.trim()} ${term}`;
        onChange({ expr: next });
      }
      setTokenMenu(null);
    },
    [tokenMenu, tabLevels, tab.expr, onChange]
  );
  const closeTokenMenu = useCallback(() => setTokenMenu(null), []);

  /* ---------- event actions ---------- */
  const onJump = (ev: LogEvent) => {
    setEventDrawerOpen(false);
    // Locate the event in the visible raw window, then map to the
    // aggregated display index (no-op when aggregation is off).
    const rawIdx = findVisibleIndex(visibleLines, ev);
    if (rawIdx < 0) return;
    const target = mapRawIdxToDisplayIdx(rawIdx, displayRows);
    if (target >= 0) {
      listRef.current?.scrollToIndex(target, 'center');
      listRef.current?.pulseIndex(target);
    }
  };

  /**
   * 检查点 verdict / Hit → 跳转到对应日志行。
   *
   * 流程：lineId（全局序号）→ visibleLines 中的 raw idx →
   * displayRows 的 display idx → scrollToIndex + pulse + pin。
   *
   * 失败兜底：
   *   - lineId 在过滤之外：用全量 stream.lines 找到这一行的 LogLine，
   *     提示用户清掉 filter 才能定位（避免静默无反馈）。
   *   - lineId 已被 ring buffer 淘汰：toast 提示。
   */
  const handleJumpToLine = useCallback(
    (lineId: number): void => {
      if (lineId < 0) return;
      // 仅在当前过滤后视图里找；不在则给提示
      let rawIdx = -1;
      for (let i = visibleLines.length - 1; i >= 0; i -= 1) {
        if (visibleLines[i].id === lineId) {
          rawIdx = i;
          break;
        }
      }
      if (rawIdx < 0) {
        const inAll = stream.lines.some((l) => l.id === lineId);
        if (inAll) {
          // eslint-disable-next-line no-console
          console.info('[checkpoint] 命中行不在当前过滤视图内；清空过滤后再试');
        } else {
          // eslint-disable-next-line no-console
          console.info('[checkpoint] 命中行已被 ring buffer 淘汰，无法定位');
        }
        return;
      }
      const target = mapRawIdxToDisplayIdx(rawIdx, displayRows);
      if (target < 0) return;
      setSelectedLineId(lineId);
      // pin 触发的临时冻结跟着同帧切到 true（与 onSelectChange 行为对齐）
      setPinPaused(true);
      // 切到非自动跟随，避免下一帧被 stream 顶到底部
      if (tab.autoScroll) onChange({ autoScroll: false });
      listRef.current?.scrollToIndex(target, 'center');
      listRef.current?.pulseIndex(target);
    },
    [visibleLines, displayRows, stream.lines, tab.autoScroll, onChange],
  );
  const onCopySummary = (ev: LogEvent) => {
    void navigator.clipboard.writeText(ev.summary).catch(() => {});
  };
  const onCopyStack = (ev: LogEvent) => {
    void navigator.clipboard.writeText(ev.snippet).catch(() => {});
  };
  const onFilterProcess = (ev: LogEvent) => {
    if (!ev.pid) return;
    onChange({ expr: `pid:${ev.pid}` });
    setEventDrawerOpen(false);
  };
  const onSaveContext = async (ev: LogEvent) => {
    // Locate the event in the *raw* ring buffer (best effort) and slice
    // a context window around it.
    const raw = stream.lines;
    const center = findVisibleIndex(raw, ev);
    if (center < 0) {
      alert('未能在缓冲中定位该事件，可能已被淘汰。');
      return;
    }
    const a = Math.max(0, center - CONTEXT_PRE);
    const b = Math.min(raw.length - 1, center + Math.max(CONTEXT_POST, ev.endIdx - ev.startIdx));
    const lines = raw.slice(a, b + 1).map((l) => l.raw);
    const safeKind = ev.kind;
    const exShort = ev.exceptionType?.split('.').pop() ?? 'event';
    const stamp = (ev.ts.match(/(\d{2}-\d{2})\s*(\d{2}):(\d{2}):(\d{2})/) ?? []).slice(1).join('') || 'now';
    const prefix = `crash-${safeKind}-${exShort}-${stamp}`;
    try {
      const r = await filesApi.saveLog({ serial, lines, prefix });
      onSaved(r.meta);
      setEventDrawerOpen(false);
    } catch (e) {
      alert((e as Error).message);
    }
  };

  /* ---------- UI ---------- */
  // Compact "WS connection" indicator: just a colored dot + tooltip, saves
  // ~70px of horizontal real estate vs the old badge.
  const wsTone =
    stream.status === 'open'
      ? 'bg-emerald-500'
      : stream.status === 'connecting'
        ? 'bg-amber-500 animate-pulse'
        : 'bg-rose-500';
  const wsLabel =
    stream.status === 'open'
      ? 'WS 已连接'
      : stream.status === 'connecting'
        ? 'WS 连接中'
        : `WS 已断开${stream.statusMessage ? ` · ${stream.statusMessage}` : ''}`;
  const StatusDot = (
    <span
      className="inline-flex items-center"
      title={wsLabel}
    >
      <span className={clsx('h-2 w-2 rounded-full', wsTone)} aria-label={wsLabel} />
    </span>
  );

  /** Icon-only button helper class — `btn` was px-3/py-1.5; we override to py-1
   * and keep gap-1.5 so iconography stays scannable. */
  const iconBtn =
    'inline-flex items-center justify-center rounded-md border border-line bg-bg-card px-2 py-1 text-ink hover:bg-bg-soft transition disabled:opacity-50 disabled:cursor-not-allowed';
  /** Active variant for toggle icon buttons — solid accent fill + white text
   * gives unmistakable on/off contrast (the earlier border-only style was
   * almost invisible on dark backgrounds). */
  const iconBtnActive =
    'inline-flex items-center justify-center rounded-md border border-accent bg-accent px-2 py-1 text-white shadow-sm transition hover:bg-accent/90';

  return (
    // Flex column that takes the whole remaining viewport height; the
    // LogList wrapper below is flex-1 + min-h-0 so it scrolls internally
    // while the toolbar / FilterBar stay pinned at the top. This is the
    // entire point of removing the page-level scroll: only ONE scrollbar
    // visible at a time, owned by the log viewport.
    <div className="flex flex-1 min-h-0 flex-col gap-1.5 rounded-md border border-line border-t-0 bg-bg-card p-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          className={clsx(
            'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition disabled:opacity-50',
            pausedForView
              ? 'border-amber-500 bg-amber-500 text-white shadow-sm hover:bg-amber-500/90'
              : 'border-line bg-bg-card text-ink hover:bg-bg-soft'
          )}
          onClick={() => {
            // 三态点击语义：
            //   1) manual=true       → 用户主动恢复，关持久化冻结
            //   2) manual=false, pin=true → 当前因 pin 临时冻结，按钮意图是
            //      「我要持久化这次冻结」，把 manual 翻 true（pin 保留，
            //      解锁行后只剩 manual 撑住）
            //   3) 两者都 false      → 用户主动冻结，开持久化
            if (manualPaused) {
              onChange({ paused: false });
            } else if (pinPaused) {
              onChange({ paused: true });
            } else {
              onChange({ paused: true });
            }
          }}
          disabled={!isOnline}
          title={
            pausedForView
              ? pinPaused && !manualPaused
                ? '已锁定行；解锁该行可恢复，或点此持久化为手动冻结'
                : `恢复显示新行${pauseBacklog > 0 ? `（已堆积 ${pauseBacklog.toLocaleString()} 行）` : ''}`
              : '冻结当前视图'
          }
        >
          {pausedForView ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
          {/* The amber active style already screams "you have backlog";
              the +N number was both ugly (constantly growing) and a
              source of toolbar jitter. Tooltip still shows the count. */}
          <span>{pausedForView ? '恢复' : '冻结'}</span>
        </button>
        <button
          className={iconBtn}
          onClick={() => stream.clear()}
          disabled={stream.lines.length === 0}
          title="清空当前缓冲（共享给所有标签）"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
        <button
          className={tab.wrap ? iconBtnActive : iconBtn}
          // wrap 切换会让 LogList 在虚拟列表（FixedSize）和"全 mount"两种
          // 形态间切换；尤其打开 wrap 时需要给 buffer 里的每一行 mount 一个
          // WrapRow（带 content-visibility:auto 走浏览器跳过离屏 layout，
          // 但 React 这一侧仍要做 N 次 reconciliation）。用 startTransition
          // 把这次 state 切换标成低优先级：用户点击是即时反馈，但 LogList
          // 重渲染会被 React 18 的 concurrent scheduler 切片，期间页面继续
          // 响应滚动 / 点击 / 输入，避免一次性长任务卡死整个页面。
          onClick={() => startTransition(() => onChange({ wrap: !tab.wrap }))}
          title={tab.wrap ? '关闭自动换行' : '启用自动换行'}
        >
          <WrapText className="h-3.5 w-3.5" />
        </button>
        <button
          className={tab.autoScroll ? iconBtnActive : iconBtn}
          onClick={() => onChange({ autoScroll: !tab.autoScroll })}
          title={tab.autoScroll ? '跟随最新（点击锁定当前位置）' : '锁定（点击恢复跟随）'}
        >
          <ArrowDownToLine className="h-3.5 w-3.5" />
        </button>
        <button
          className={aggregateOn ? iconBtnActive : iconBtn}
          onClick={() => onChange({ aggregate: !aggregateOn })}
          title={
            aggregateOn
              ? '当前已折叠连续重复行；点击展开查看每一行'
              : '折叠连续重复行（× N），减少无意义滚动'
          }
        >
          <Layers className="h-3.5 w-3.5" />
        </button>
        {/* Jump-to-top / jump-to-bottom live in the toolbar (right next to
            层叠) instead of floating over the log content. Top jump is
            non-animated by request — the user wants instant feedback when
            they reach for the start of a buffer. The button also clears
            any pinned row so the viewport isn't fighting a freeze. */}
        <button
          className={iconBtn}
          onClick={() => {
            setSelectedLineId(null);
            setPinPaused(false);
            listRef.current?.scrollToIndex(0, 'start', false);
          }}
          title="跳到顶部"
        >
          <ChevronsUp className="h-3.5 w-3.5" />
        </button>
        <button
          className={iconBtn}
          onClick={() => listRef.current?.scrollToBottom(false)}
          title="跳到底部"
        >
          <ChevronsDown className="h-3.5 w-3.5" />
        </button>

        {/* Level toggle group — on/off must be unmistakable: on uses a saturated
            tone background with bold colored text; off uses a line-through
            strike on dim ink, so users immediately read "hidden" rather than
            "barely-different shade of the same". */}
        <div className="ml-0.5 inline-flex overflow-hidden rounded-md border border-line">
          {ALL_LEVELS.map((lv) => {
            const on = tabLevels.includes(lv);
            return (
              <button
                key={lv}
                className={clsx(
                  'px-2 py-1 font-mono text-[11px] transition',
                  on
                    ? levelOnClass(lv)
                    : 'bg-bg-card text-ink-dim/60 line-through hover:bg-bg-soft',
                  lv !== 'E' && 'border-r border-line'
                )}
                onClick={() => {
                  const next = on
                    ? tabLevels.filter((x) => x !== lv)
                    : [...tabLevels, lv];
                  const cleaned: LevelToggle[] = ALL_LEVELS.filter((l) => next.includes(l));
                  onChange({ levels: cleaned.length > 0 ? cleaned : [lv] });
                }}
                title={`${on ? '隐藏' : '显示'} Level ${lv}`}
              >
                {lv}
              </button>
            );
          })}
        </div>

        <button
          className={searchOpen ? iconBtnActive : iconBtn}
          onClick={() => setSearchOpen((v) => !v)}
          title="在当前视图内查找 (Ctrl/Cmd+F)"
        >
          <Search className="h-3.5 w-3.5" />
        </button>
        <button
          className={clsx(
            'relative inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition',
            eventBadge > 0
              ? 'border-rose-500 bg-rose-500/10 text-rose-500 animate-[pulse-row_1s_ease-out_1]'
              : 'border-line bg-bg-card text-ink hover:bg-bg-soft'
          )}
          onClick={() => setEventDrawerOpen(true)}
          title="事件中心（崩溃 / ANR / OOM / LMK）"
        >
          <AlertOctagon className="h-3.5 w-3.5" />
          {eventBadge > 0 && <span className="font-mono text-[11px]">{eventBadge}</span>}
        </button>
        <button
          className={iconBtn}
          disabled={busy || filtered.length === 0}
          onClick={onSaveCurrent}
          title="保存当前日志为文件"
        >
          <Save className={clsx('h-3.5 w-3.5', busy && 'animate-pulse')} />
        </button>
        <RuleEntryBadge
          checkpointMounted={mountedSuites.length}
          pass={checkpointSummary.pass}
          mid={checkpointSummary.mid}
          fail={checkpointSummary.fail}
          kvKeys={watchController.keys.length}
          kvHits={watchTotalHits}
          active={panelVisible}
          onClick={() => {
            // 单按钮 toggle：开 → 关 / 关 → 开。
            // tab 由内部 state（rightPanelTab，持久化到 prefs）记忆；
            // 用户上次留在哪个 tab，下次打开就回到哪个。
            setPanelVisible((v) => !v);
          }}
        />
        <div className="ml-auto">{StatusDot}</div>
      </div>

      <FilterBar
        expr={tab.expr}
        errors={compiled.errors}
        saved={savedFilters}
        onChange={(v) => onChange({ expr: v })}
        onSave={onSaveFilter}
        onApplySaved={onApplySaved}
        onDeleteSaved={onDeleteSaved}
        tagSuggestions={tagSuggestions}
        combine={tabCombine}
        onCombineChange={(v) => onChange({ combine: v })}
      />

      {searchOpen && (
        <SearchBar
          query={searchQuery}
          onChange={setSearchQuery}
          total={searchMatches.length}
          active={searchActive}
          onNext={() => stepSearch(1)}
          onPrev={() => stepSearch(-1)}
          onClose={() => {
            setSearchOpen(false);
            setSearchQuery('');
          }}
        />
      )}

      {/* 旧的 CheckpointStatusStrip 折叠条已下线：原本在 panelVisible=false
          时显示"检查点 · 1 个套件 · 等待 1 · 展开"；视觉太重，跟工具栏右
          上角的"检查点"按钮 + 红色徽标已重复。展开入口改成走工具栏，腾出
          屏幕空间给日志列表。*/}

      <div className="flex flex-1 min-h-0 gap-2">
      {/* Log viewport. flex-1 + min-h-0 lets it consume the leftover
          vertical space of the LogcatTabBody flex column; we then measure
          this wrapper's height with ResizeObserver and pass it down to
          react-window's FixedSizeList (which requires an explicit pixel
          height). The footer is rendered INSIDE this wrapper so its own
          rows stay aligned with the list border, but we subtract its
          height from `logListHeight` below. */}
      <div ref={logViewportRef} className="relative flex flex-1 min-w-0 min-h-0 flex-col overflow-hidden rounded-md border border-line">
        <LogList
          ref={listRef}
          lines={displayRows}
          wrap={tab.wrap}
          autoScroll={tab.autoScroll && !pausedForView}
          height={logListHeight}
          onStickyChange={setSticky}
          onPendingNewChange={setPendingNew}
          onTokenClick={handleTokenClick}
          searchQuery={searchOpen ? searchQuery : ''}
          selectedIndex={selectedIndex}
          onSelectChange={(idx) => {
            // 同帧 batch：避免原先「setSelectedLineId → useEffect → onChange
            // ({paused:true}) → prefs round-trip → tab.paused=true → 下一帧
            // visibleLines 才切到 frozen 快照」中间 1-2 帧的 race window，
            // 那段窗口里 itemCount effect 已视 pin 生效（停止跟尾），但
            // visibleLines 还是 live filtered，列表会被推走 / 视觉空白。
            if (idx === null) {
              setSelectedLineId(null);
              setPinPaused(false);
              return;
            }
            const lineId = displayRows[idx]?.id ?? null;
            if (lineId === null) return;
            setSelectedLineId(lineId);
            setPinPaused(true);
            // 主动把 pin 行滚到视口中央，进一步防御「冻结后 viewport 卡在
            // 错位 scrollTop」的视觉空白。next frame 等 frozen 快照已
            // commit、displayRows 已用快照重新 aggregate 后再 scroll。
            requestAnimationFrame(() => {
              listRef.current?.scrollToIndex(idx, 'center', false);
              listRef.current?.pulseIndex(idx);
            });
          }}
          markerFor={markerFor}
          onRowContextMenu={handleRowContextMenu}
          multiSelected={multiSelected}
          onMultiSelectChange={handleMultiSelectChange}
        />
        {/* Lock banner — only when a row is pinned. Top-left so it's
            unmistakable against the toolbar/header to the right. The pin
            freezes the viewport (sticky=false); streamed lines pile up
            silently in the background. Surface that backlog right here
            ("+1,234") so the user knows the stream is still alive and
            give them a one-click escape ("跳到底部") that both clears
            the pin and snaps to the tail. */}
        {selectedIndex !== null && (
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              if (selectedIndex === null) return;
              listRef.current?.scrollToIndex(selectedIndex, 'center');
              listRef.current?.pulseIndex(selectedIndex);
            }}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && selectedIndex !== null) {
                e.preventDefault();
                listRef.current?.scrollToIndex(selectedIndex, 'center');
                listRef.current?.pulseIndex(selectedIndex);
              }
            }}
            className="absolute left-2 top-2 z-20 inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-emerald-500 bg-emerald-500/20 px-2.5 py-1 text-[11px] font-medium text-emerald-300 shadow-md backdrop-blur-sm transition hover:bg-emerald-500/30"
            title="点击跳回锁定行 · Esc 解除"
          >
            <Pin className="h-3 w-3" />
            <span className="tabular-nums">#{(selectedIndex + 1).toLocaleString()}</span>
            {pendingNew > 0 && (
              <>
                <span className="text-emerald-200/60">·</span>
                <span
                  className="tabular-nums text-amber-300"
                  title="锁定期间收到的新日志数量（点击右下角 +N 跳到底部）"
                >
                  +{pendingNew.toLocaleString()}
                </span>
              </>
            )}
            <button
              type="button"
              className="ml-0.5 rounded-full p-0.5 text-emerald-200 transition hover:bg-emerald-500/30 hover:text-white"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedLineId(null);
                setPinPaused(false);
              }}
              title="解除锁定 (Esc)"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
        {/* Floating "+N 条新日志" CTA — only when the user has scrolled
            away from the tail AND there is real backlog to look at. Jump
            and clear-pin behaviour is the same as the toolbar bottom
            button, but the inline count is the whole point. The legacy
            floating ChevronsUp/Down buttons have moved into the toolbar
            so the log content is no longer covered. */}
        {!sticky && pendingNew > 0 && (
          <button
            className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-1 rounded-full border border-accent bg-accent px-3 py-1 text-[11px] font-medium text-white shadow-lg transition hover:bg-accent/90 tabular-nums"
            onClick={() => {
              // 用户主动点 +N 表达"回到最新并继续跟尾"。四件事：
              //   1) 解除 pin（让 scrollToBottom 不被锁定 hold 住）；
              //   2) 解除 pinPaused（pin 触发的临时冻结要跟着 pin 同走）；
              //   3) 恢复 tab.autoScroll=true，否则 scrollToBottom 后只是
              //      瞬时贴到当前 maxTop，后续 chunk 还是不会带着滚——
              //      用户点完会"看到一下又卡住"，体感上像没用；
              //   4) listRef.current.scrollToBottom 立刻贴底。
              // 注意：不动 manualPaused —— 用户手动冻结的话 +N 按钮本身不
              //   会出现（autoScroll 已经被冻结挡住，sticky 仍是 true），
              //   走到这里说明只是 pin 临时冻结。
              setSelectedLineId(null);
              setPinPaused(false);
              if (!tab.autoScroll) onChange({ autoScroll: true });
              listRef.current?.scrollToBottom(false);
            }}
            title="回到最新日志（瞬移）"
          >
            <ArrowDownToLine className="h-3.5 w-3.5" />
            {pendingNew.toLocaleString()} 条新日志
          </button>
        )}
        {/* Footer: only line-count info, never wraps, all numeric spans use
            tabular-nums so digits don't shift the right edge as counts grow.
            Filter expr is already echoed in the FilterBar input above — no
            need to repeat it here. */}
        <div className="flex items-center justify-between gap-3 border-t border-line bg-bg-soft px-3 py-1 text-[11px] text-ink-muted">
          <span className="tabular-nums">
            {hasFilter
              ? `命中 ${filtered.length.toLocaleString()} / 累计 ${matchedTotal.toLocaleString()}`
              : `${filtered.length.toLocaleString()} 行`}
            {aggregateOn && displayRows.length < foldedTotal && (
              <> · 折叠后 {displayRows.length.toLocaleString()}</>
            )}
            {pausedForView && ' · 已冻结'}
            {tabLevels.length < ALL_LEVELS.length && ` · 仅 ${tabLevels.join('/')}`}
          </span>
          <span className="tabular-nums text-ink-dim" title="原始流累计接收行数（不含过滤）">
            Σ {stream.totalSeen.toLocaleString()}
          </span>
        </div>
      </div>

      {/* 右侧 tabbed 面板：检查点 / 日志摘要 */}
      {panelVisible && (
        <div className="flex w-[420px] min-w-[360px] max-w-[520px] shrink-0 flex-col gap-1 overflow-hidden rounded-md border border-line bg-bg-card">
          <div className="flex items-stretch border-b border-line bg-bg-soft text-[11px]">
            <button
              type="button"
              className={clsx(
                'flex-1 px-3 py-1.5 transition',
                panelTab === 'checkpoint'
                  ? 'border-b-2 border-accent font-semibold text-ink'
                  : 'text-ink-muted hover:bg-bg-card',
              )}
              onClick={() => setPanelTab('checkpoint')}
            >
              检查规则 {mountedSuites.length > 0 && <span className="font-mono text-ink-dim">· {mountedSuites.length}</span>}
            </button>
            <button
              type="button"
              className={clsx(
                'flex-1 px-3 py-1.5 transition',
                panelTab === 'watch'
                  ? 'border-b-2 border-accent font-semibold text-ink'
                  : 'text-ink-muted hover:bg-bg-card',
              )}
              onClick={() => setPanelTab('watch')}
            >
              取值规则 {watchController.keys.length > 0 && <span className="font-mono text-ink-dim">· {watchController.keys.length}</span>}
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            {panelTab === 'checkpoint' && (
              <CheckpointPanel
                serial={serial}
                currentPackage={currentPackage}
                mounted={mountedSuites}
                setMounted={setMountedSuites}
                suiteCache={suiteCache}
                setSuiteCache={setSuiteCache}
                snapshots={checkpointController.snapshots}
                hitsByRun={checkpointController.hitsByRun}
                nextLineId={checkpointController.nextLineId}
                busy={suiteFetchBusy}
                onClose={() => setPanelVisible(false)}
                onJumpToLine={handleJumpToLine}
                paused={pausedForView}
                onUnpause={() => {
                  // 一键完全解冻：清 manualPaused + pinPaused + 锁定行。
                  // 之前只清 manualPaused 导致 pin 触发的冻结无法被检查点
                  // 面板的"恢复"按钮解开，与按钮文案不符。
                  onChange({ paused: false });
                  setPinPaused(false);
                  setSelectedLineId(null);
                }}
              />
            )}
            {panelTab === 'watch' && (
              <WatchPanel
                keys={watchController.keys}
                snapshot={watchController.snapshot}
                onAddKey={watchController.addKey}
                onRemoveKey={watchController.removeKey}
                onClearKeys={watchController.clearKeys}
                nextLineId={watchController.nextLineId}
                onClose={() => setPanelVisible(false)}
                onJumpToLine={handleJumpToLine}
              />
            )}
          </div>
        </div>
      )}
      </div>

      {modalPayload && (
        <LineToRuleModal
          payload={modalPayload}
          currentPackage={currentPackage}
          onClose={() => setModalPayload(null)}
          onSaved={(savedSuiteId) => {
            setModalPayload(null);
            setMultiSelected(new Set());
            dismissSelection();
            // 刚保存的套件可能是已经挂载的（追加规则），让 suiteCache 重拉
            // 该 id，从而把新规则推到 RunnerSet 并回放历史让 verdict 更新。
            setSuiteCache((prev) => {
              if (!savedSuiteId || !prev[savedSuiteId]) return prev;
              const next = { ...prev };
              delete next[savedSuiteId];
              return next;
            });
          }}
        />
      )}

      {selectionPayload && !modalPayload && (
        <LogSelectionPopover
          payload={selectionPayload}
          onAddKeyword={(text, line) => {
            setModalPayload({ mode: 'selection', line, text });
          }}
          onAddSequence={(lines) => {
            setModalPayload({ mode: 'multi-line-sequence', lines });
          }}
          onDismiss={dismissSelection}
        />
      )}

      {multiSelected.size > 0 && !modalPayload && !selectionPayload && (
        <MultiSelectActionBar
          count={multiSelected.size}
          onMakeSequence={() => {
            const indices = Array.from(multiSelected).sort((a, b) => a - b);
            const lines: LogLine[] = [];
            for (const i of indices) {
              const ln = displayRows[i];
              if (ln) lines.push(ln);
            }
            if (lines.length > 0) {
              setModalPayload({ mode: 'multi-line-sequence', lines });
            }
          }}
          onMakeKeywords={() => {
            const indices = Array.from(multiSelected).sort((a, b) => a - b);
            const lines: LogLine[] = [];
            for (const i of indices) {
              const ln = displayRows[i];
              if (ln) lines.push(ln);
            }
            if (lines.length > 0) {
              setModalPayload({ mode: 'multi-line-keywords', lines });
            }
          }}
          onClear={() => setMultiSelected(new Set())}
        />
      )}

      {tokenMenu && (
        <TokenMenu
          field={tokenMenu.field}
          value={tokenMenu.value}
          anchor={tokenMenu.rect}
          onPick={onTokenPick}
          onClose={closeTokenMenu}
        />
      )}

      <EventDrawer
        open={eventDrawerOpen}
        onClose={() => setEventDrawerOpen(false)}
        store={eventStore}
        onJump={onJump}
        onCopySummary={onCopySummary}
        onCopyStack={onCopyStack}
        onFilterProcess={onFilterProcess}
        onSaveContext={onSaveContext}
      />
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Helpers                                                              */
/* -------------------------------------------------------------------- */

function quoteIfSpace(v: string): string {
  return /\s|[:=]/.test(v) ? `"${v}"` : v;
}

function levelOnClass(lv: LevelToggle): string {
  switch (lv) {
    case 'V':
      return 'bg-slate-500/35 text-slate-200 font-bold';
    case 'D':
      return 'bg-sky-500/35 text-sky-300 font-bold';
    case 'I':
      return 'bg-emerald-500/35 text-emerald-300 font-bold';
    case 'W':
      return 'bg-amber-500/35 text-amber-300 font-bold';
    case 'E':
      return 'bg-rose-500/40 text-rose-200 font-bold';
  }
}

/**
 * Locate an event in a slice of lines. The event's `startIdx` is an
 * absolute counter that doesn't necessarily map cleanly to the current
 * raw-ring offset (the ring evicts). We fall back to a content-based
 * heuristic: scan from the end for the first row that contains the
 * exception type / signal / "FATAL EXCEPTION" / etc. and that matches
 * the event's pid when available.
 */
function findVisibleIndex(lines: LogLine[], ev: LogEvent): number {
  const needle =
    ev.exceptionType ??
    ev.signal ??
    (ev.kind === 'java_crash' ? 'FATAL EXCEPTION' : ev.kind === 'anr' ? 'ANR in' : '');
  if (!needle) return -1;
  for (let i = lines.length - 1; i >= 0; i--) {
    if (ev.pid && lines[i].pid && lines[i].pid !== ev.pid) continue;
    if (lines[i].raw.includes(needle)) return i;
  }
  return -1;
}

/* -------------------------------------------------------------------- */
/* Saved logs (files)                                                  */
/* -------------------------------------------------------------------- */

interface SavedProps {
  serial: string | undefined;
  openFile: FileMeta | undefined;
  setOpenFile: (f: FileMeta | undefined) => void;
}

function SavedLogs({ serial, openFile, setOpenFile }: SavedProps) {
  const { data, refetch, isFetching } = useQuery({
    queryKey: ['files', serial, 'logcat'],
    queryFn: () => filesApi.list({ serial: serial!, kind: 'logcat' }),
    enabled: !!serial,
  });

  const [search, setSearch] = useState('');
  const [text, setText] = useState<string[]>([]);
  const [meta, setMeta] = useState<{ totalSize: number; reachedEnd: boolean } | undefined>();
  const [preview, setPreview] = useState<FileMeta | undefined>();
  const items = data?.items ?? [];

  useEffect(() => {
    if (!openFile) return;
    setText([]);
    filesApi.text({ path: openFile.path, limit: 50_000 }).then((r) => {
      setText(r.lines);
      setMeta({ totalSize: r.totalSize, reachedEnd: r.reachedEnd });
    });
  }, [openFile]);

  const filtered = useMemo(() => {
    if (!search) return text;
    const q = search.toLowerCase();
    return text.filter((l) => l.toLowerCase().includes(q));
  }, [text, search]);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_2fr]">
      <div>
        <FilesTable
          items={items}
          onOpen={(f) => setOpenFile(f)}
          onChanged={() => {
            setOpenFile(undefined);
            refetch();
          }}
        />
        <div className="mt-2 text-right">
          <button className="btn" onClick={() => refetch()} disabled={!serial}>
            <RotateCcw className={clsx('h-3.5 w-3.5', isFetching && 'animate-spin')} />
            <span>刷新列表</span>
          </button>
        </div>
      </div>
      <div className="space-y-2">
        {!openFile && <div className="card-pad text-sm text-ink-muted">从左侧选择一个日志文件查看。</div>}
        {openFile && (
          <div className="card-pad space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-mono text-sm">{openFile.name}</p>
                <p className="text-xs text-ink-muted">
                  {meta
                    ? `${(meta.totalSize / 1024).toFixed(1)} KB · ${filtered.length.toLocaleString()} 行${!meta.reachedEnd ? '（已截断）' : ''}`
                    : '加载中…'}
                </p>
              </div>
              <div className="inline-flex items-center gap-2">
                <Search className="h-3.5 w-3.5 text-ink-dim" />
                <input
                  className="input w-56"
                  placeholder="搜索"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button className="btn" onClick={() => setPreview(openFile)}>
                  完整预览
                </button>
              </div>
            </div>
            <div className="max-h-[60vh] overflow-auto rounded-md bg-bg-soft p-2 font-mono text-[11px] leading-snug">
              {filtered.map((l, i) => (
                <pre
                  key={i}
                  className={clsx(
                    'whitespace-pre-wrap break-all',
                    l.includes('FATAL') && 'text-rose-500'
                  )}
                >
                  {l}
                </pre>
              ))}
            </div>
          </div>
        )}
      </div>
      {preview && (
        <FilePreviewModal
          source={{ kind: 'pc', meta: preview }}
          caption="日志"
          onClose={() => setPreview(undefined)}
        />
      )}
    </div>
  );
}
